/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ContentInfoCardKernel,
    InfoCardKernel,
    InfoCardKernelType,
    OfferInfoCardKernel,
    UnifiedCollectionInfoCardKernel,
    VodOfferInfoCardKernel,
} from 'client-code/actions';
import {
    ContentVodItemKernel,
    OfferVodItemKernel,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemKernelType,
    VodItemType,
} from 'client-code/vod-browse';

import {
    IInfoCard,
    IInfoCardProperty,
    InfoCardManager,
    InfoCardUserInputHandler,
} from '../infocard';

export class VodBrowseInfoCardUserInputHandler extends InfoCardUserInputHandler {
    private mInfoCard: IInfoCard = InfoCardManager.getInstance();

    private horizontalKeyNavigationHelper(direction: 'left' | 'right'): void {
        const { activeItemIndex, itemList } = (this.mInfoCard.getContextData(
            VodBrowseInfoCardUserInputHandler.name,
        ) || {}) as { activeItemIndex: number; itemList: VodItem[] };

        // activeItemIndex is always a non-folder item and navigableItems is always > 1.
        const navigableItems = itemList.filter(
            (item) => item.vodItemType.toLowerCase() !== VodItemType.FolderVodItem.toLowerCase(),
        );
        const currentNavigableIndex = navigableItems.indexOf(itemList[activeItemIndex]);
        const nextNavigableIndex =
            direction === 'left'
                ? (currentNavigableIndex - 1 + navigableItems.length) % navigableItems.length
                : (currentNavigableIndex + 1) % navigableItems.length;
        const nextItem = navigableItems[nextNavigableIndex];
        const nextItemIndex = itemList.indexOf(nextItem);

        this.mInfoCard.hideActionButtons();
        this.mInfoCard.setInfoCardKernel(this.getInfoCardKernel(nextItem));
        this.mInfoCard.setContextData(VodBrowseInfoCardUserInputHandler.name, {
            activeItemIndex: nextItemIndex,
            itemList,
        });
    }

    /**
     * @override
     */
    public onKeyLeft(properties: IInfoCardProperty): void {
        if (!properties.hasItemsBefore) return;
        this.horizontalKeyNavigationHelper('left');
    }

    /**
     * @override
     */
    public onKeyRight(properties: IInfoCardProperty): void {
        if (!properties.hasItemsAfter) return;
        this.horizontalKeyNavigationHelper('right');
    }

    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernel(item: VodItem): InfoCardKernel {
        switch (item.vodItemType.toLowerCase()) {
            case VodItemType.EpisodeVodItem.toLowerCase(): {
                if (
                    item.vodItemKernel?.vodItemKernelType === VodItemKernelType.OfferVodItemKernel
                ) {
                    return {
                        infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
                        offerId: (item.vodItemKernel as OfferVodItemKernel).offerId,
                    } as OfferInfoCardKernel;
                }
                const { contentId, partnerId, collapsedVodItemId } =
                    item.vodItemKernel as ContentVodItemKernel;
                return {
                    infoCardKernelType: InfoCardKernelType.ContentInfoCardKernel,
                    contentId,
                    partnerId,
                    collapsedVodItemId,
                } as ContentInfoCardKernel;
            }

            case VodItemType.MovieVodItem.toLowerCase():
            case VodItemType.SpecialVodItem.toLowerCase():
                return {
                    infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
                    offerId: (item.vodItemKernel as OfferVodItemKernel).offerId,
                } as VodOfferInfoCardKernel;

            default: {
                const { unifiedCollectionId, partnerId, collapsedVodItemId } =
                    item.vodItemKernel as UnifiedCollectionVodItemKernel;
                return {
                    infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                    unifiedCollectionId,
                    partnerId,
                    collapsedVodItemId,
                } as UnifiedCollectionInfoCardKernel;
            }
        }
    }
}
